// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import Header from './components/layout/Header';
import StoreConnectionPage from './pages/StoreConnectionPage';
import ProductAnalyticsPage from './pages/ProductAnalyticsPage';
import KeywordMetricsPage from './pages/KeywordMetricsPage';
import RecommendationsPage from './pages/RecommendationsPage';
import ImportedProductsPage from './pages/ImportedProductsPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import TaobaoSearchPage from './pages/TaobaoSearchPage';
import Model3DCreationPage from './pages/Model3DCreationPage';
import ProductImportPage from './pages/ProductImportPage';
import ExtensionsPage from './pages/ExtensionsPage';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AccountPage from './pages/AccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { API_URL, initApiClient } from './utils/api';
import { isEmbeddedApp, isAppBridgeAvailable, initializeAppBridge, getShopDomain } from './utils/shopifyAppBridge';

console.log('[DEBUG] API_URL in App.js:', API_URL);

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

function App() {
  const [activeTab, setActiveTab] = useState('store-connection');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authView, setAuthView] = useState('login'); // 'login' | 'signup' | 'forgot' | 'reset'
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState('');

  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('🚀 App initialization starting...');
        console.log('Checking embedded app status...');
        console.log('isEmbeddedApp():', isEmbeddedApp());
        console.log('isAppBridgeAvailable():', isAppBridgeAvailable());
        console.log('window.shopify:', typeof window !== 'undefined' ? window.shopify : 'window undefined');

        // Initialize Shopify App Bridge if embedded - try to load it even if not available yet
        if (isEmbeddedApp()) {
          console.log('Embedded app detected, attempting App Bridge initialization...');

          try {
            // Initialize App Bridge with proper async handling and timeout
            const appBridge = await Promise.race([
              initializeAppBridge(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('App Bridge initialization timeout')), 5000)
              )
            ]);

            if (appBridge) {
              const shop = getShopDomain();
              console.log('App Bridge initialized successfully for shop:', shop);

              // For embedded apps, we'll use session tokens instead of stored JWT
              // The session token will be automatically handled by our API utility
              setCurrentUser({
                id: 'embedded-user',
                email: `admin@${shop}`,
                role: 'ADMIN',
                isEmbedded: true,
                shop: shop
              });

              // Initialize API client for embedded mode
              initApiClient({
                id: 'embedded-user',
                email: `admin@${shop}`,
                role: 'ADMIN',
                isEmbedded: true,
                shop: shop
              });

              setLoading(false);
              return;
            } else {
              console.warn('App Bridge initialization returned null - falling back to standalone mode');
            }
          } catch (error) {
            if (error.message === 'App Bridge initialization timeout') {
              console.error('App Bridge initialization timed out after 5 seconds - falling back to standalone mode');
            } else {
              console.error('App Bridge initialization failed:', error);
              console.log('Falling back to standalone mode');
            }
          }
        }

        // Attempt to restore session for non-embedded or fallback
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            const res = await fetch(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
              credentials: 'include',
            });
            const data = await res.json();
            if (data?.user) {
              setCurrentUser(data.user);
              localStorage.setItem('user', JSON.stringify(data.user));
              initApiClient(data.user);
            } else {
              // invalid token
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
            }
          } catch (error) {
            console.error('Failed to fetch auth/me:', error);
            // Continue without auth - user can log in
          }
        }

      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');

    if (tokenParam && emailParam) {
      setResetToken(tokenParam);
      setResetEmail(emailParam);
      setAuthView('reset');

      // Clean the query params so refresh doesn't re-trigger
      params.delete('token');
      params.delete('email');
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, document.title, newUrl);
    } else if (tokenParam && params.get('shop')) {
      // Handle Shopify App Install Redirect - Capture Token
      console.log('✅ Captured new auth token from URL');
      localStorage.setItem('authToken', tokenParam);

      // Clear token from URL to clean up
      params.delete('token');
      params.delete('shop');
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, document.title, newUrl);

      // Force reload to pick up new token? Or just let the existing useEffect attempt to use it?
      // Since useEffect [] runs once, we might need to manually trigger auth check or reload.
      window.location.reload();
    }
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // Update user context when it changes
  const updateUserContext = (user) => {
    setCurrentUser(user);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  };

  const renderActivePage = () => {
    switch (activeTab) {
      case 'store-connection':
        return <StoreConnectionPage
          currentUser={currentUser}
          onUserUpdate={updateUserContext}
        />;
      case 'product-analytics': return <ProductAnalyticsPage />;
      case 'keyword-metrics': return <KeywordMetricsPage />;
      case 'recommendations': return <RecommendationsPage />;
      case 'imported-products': return <ImportedProductsPage />;
      case 'settings': return <StoreSettingsPage />;
      case 'taobao-search': return <TaobaoSearchPage />;
      case '3d-model': return <Model3DCreationPage />;
      case 'product-import': return <ProductImportPage />;
      case 'extensions': return <ExtensionsPage />;
      case 'account': return <AccountPage />;
      default: return <StoreConnectionPage />;
    }
  };

  const handleAuthSuccess = (user /*, token already stored */) => {
    setCurrentUser(user);
    initApiClient(user);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {loading ? (
          <main className="py-6"><div className="max-w-7xl mx-auto px-4">Loading...</div></main>
        ) : currentUser ? (
          <>
            <Header
              activeTab={activeTab}
              handleTabClick={handleTabClick}
              setActiveTab={setActiveTab}
              currentUser={currentUser}
              onLogout={() => {
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
                setCurrentUser(null);
                setAuthView('login');
                setResetToken(null);
                setResetEmail('');
              }}
            />
            <main className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {renderActivePage()}
              </div>
            </main>
          </>
        ) : (
          <main className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {authView === 'login' && (
                <LoginPage
                  onAuthSuccess={handleAuthSuccess}
                  switchToSignup={() => setAuthView('signup')}
                  switchToForgot={() => setAuthView('forgot')}
                />
              )}
              {authView === 'signup' && (
                <SignupPage onAuthSuccess={handleAuthSuccess} switchToLogin={() => setAuthView('login')} />
              )}
              {authView === 'forgot' && (
                <ForgotPasswordPage
                  onSuccess={() => {
                    toast.success('If an account exists for that email, a reset link has been sent.');
                  }}
                  switchToLogin={() => setAuthView('login')}
                />
              )}
              {authView === 'reset' && (
                <ResetPasswordPage
                  token={resetToken}
                  email={resetEmail}
                  onSuccess={() => {
                    toast.success('Password updated successfully. Please log in with your new password.');
                    setAuthView('login');
                    setResetToken(null);
                    setResetEmail('');
                  }}
                  switchToLogin={() => {
                    setAuthView('login');
                    setResetToken(null);
                    setResetEmail('');
                  }}
                />
              )}
            </div>
          </main>
        )}
        <footer className="mt-auto border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-gray-600">
            <a
              href="https://autolistify.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Privacy Policy
            </a>
            <a
              href="https://autolistify.com/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms of Service
            </a>
          </div>
        </footer>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
